import { useLocation } from 'react-router-dom'

import { cap } from '../State'
import { translations } from '../translations'

const language = cap.language.state || 'IT'

const MENU_SECTIONS = {
  operative: { IT: 'Operativo', EN: 'Operative' },
  admin: { IT: 'Amministrazione', EN: 'Administration' },
  testing: { IT: 'Testing', EN: 'Testing' },
  system: { IT: 'Sistema', EN: 'System' }
}

/**
 * Restituisce l'etichetta localizzata di una sezione del menu
 * @param {string} sectionKey - Chiave della sezione (operative, admin, testing, system)
 * @returns {string} Etichetta tradotta nella lingua corrente
 */
export const getSectionLabel = (sectionKey) =>
  MENU_SECTIONS[sectionKey]?.[language] || MENU_SECTIONS[sectionKey]?.IT || ''

/**
 * Costruisce la mappa path → titolo per la top bar, ordinata per lunghezza decrescente (prefix match).
 * @param {string} language - Codice lingua (IT, EN, …)
 * @returns {Array<{ path: string, label: string }>} Coppie path/etichetta
 */
const buildPathTitleMap = (language) => {
  const t = translations[language] || translations.IT
  return [
    { path: '/login', label: t.login },
    { path: '/operatore/list', label: t.conversations },
    { path: '/operatore', label: t.conversations },
    { path: '/chat', label: t.chat },
    { path: '/statistics', label: t.statistics },
    { path: '/dashboardPricing', label: t.dashboardPricing },
    { path: '/process/performance', label: t.performanceMonitor },
    { path: '/process/errorStats', label: t.processErrors },
    { path: '/process/stats', label: t.processStats },
    { path: '/process/compare', label: t.processComparison },
    { path: '/process', label: t.performanceMonitor },
    { path: '/conversations/list', label: t.operations },
    { path: '/conversations', label: t.operations },
    { path: '/pricing', label: t.pricing },
    { path: '/community/panel', label: t.communityPanel },
    { path: '/community/search', label: t.communityPanel },
    { path: '/community', label: t.communityPanel },
    { path: '/admin/society/list', label: t.societyList },
    { path: '/admin/society', label: t.societyList },
    { path: '/admin/users/list', label: t.userList },
    { path: '/admin/users', label: t.userList },
    { path: '/admin/travellers/import', label: t.travellersList },
    { path: '/admin/travellers/list', label: t.travellersList },
    { path: '/admin/travellers', label: t.travellersList },
    { path: '/admin/panelOperations', label: t.panelOperations },
    { path: '/admin/coefficient', label: t.adminCoefficients },
    { path: '/admin/settings', label: t.impostazioniSistema },
    { path: '/admin', label: getSectionLabel('admin') },
    { path: '/testDyn', label: t.dynamicTest },
    { path: '/autotest/list', label: t.autoTest },
    { path: '/autotest', label: t.autoTest },
    { path: '/prompts/list', label: t.prompts },
    { path: '/prompts', label: t.prompts },
    { path: '/testChat', label: t.chat }
  ].sort((a, b) => b.path.length - a.path.length)
}

/**
 * Restituisce il titolo della pagina corrente per la top bar, con prefix matching sulle route.
 * @returns {string} Etichetta da mostrare nella top bar
 */
export const usePageTitle = () => {
  const { pathname } = useLocation()
  const language = cap.language.state || 'IT'
  if (pathname === '/') return translations[language]?.home || 'Home'

  const pathMap = buildPathTitleMap(language)
  const match = pathMap.find(({ path }) => pathname === path || pathname.startsWith(`${path}/`))

  return match?.label || translations[language]?.home || 'Home'
}

export const useMenuItems = ({ role }) =>
  [
    {
      label: translations[language].conversations,
      icon: 'fa-duotone fa-microchip-ai',
      path: '/operatore',
      role: ['admin', 'operator', 'tester', 'operatorCommunity'],
      section: 'operative'
    },
    {
      label: translations[language].chat,
      icon: 'fa-duotone fa-paper-plane',
      path: '/chat/',
      role: ['admin', 'tester'],
      section: 'operative'
    },
    {
      label: translations[language].statistics,
      icon: 'fa-duotone fa-chart-pie',
      path: '/statistics',
      role: ['admin', 'operatorCommunity', 'fatturazione'],
      section: 'operative'
    },
    {
      label: translations[language].performanceMonitor,
      icon: 'fa-duotone fa-gauge-high',
      path: '/process/performance',
      role: ['admin'],
      section: 'operative'
    },
    {
      label: translations[language].operations,
      icon: 'fa-duotone fa-list-tree',
      path: '/conversations/list',
      role: ['admin'],
      section: 'operative'
    },
    {
      label: translations[language].communityPanel,
      icon: 'fa-duotone fa-gear',
      path: '/community/search',
      role: ['admin', 'dynamicTester', 'tester', 'operatorCommunity'],
      section: 'admin'
    },
    {
      label: translations[language].societyList,
      icon: 'fa-duotone fa-briefcase',
      path: '/admin/society/list',
      role: ['admin'],
      section: 'admin'
    },
    {
      label: translations[language].userList,
      icon: 'fa-duotone fa-user-gear',
      path: '/admin/users/list',
      role: ['admin'],
      section: 'admin'
    },
    {
      label: translations[language].travellersList,
      icon: 'fa-duotone fa-users',
      path: '/admin/travellers/list',
      role: ['admin'],
      section: 'admin'
    },
    {
      label: translations[language].panelOperations,
      icon: 'fa-duotone fa-folder-gear',
      path: '/admin/panelOperations',
      role: ['admin'],
      section: 'admin'
    },
    {
      label: translations[language].adminCoefficients,
      icon: 'fa-duotone fa-hammer',
      path: '/admin/coefficient',
      role: ['admin', 'dynamicTester'],
      section: 'admin'
    },
    {
      label: translations[language].impostazioniSistema,
      icon: 'fa-duotone fa-sliders',
      path: '/admin/settings',
      role: ['admin'],
      section: 'admin'
    },
    {
      label: translations[language].dynamicTest,
      icon: 'fa-duotone fa-flask-round-poison',
      path: '/testDyn',
      role: ['admin', 'dynamicTester', 'tester'],
      section: 'testing'
    },
    {
      label: translations[language].autoTest,
      icon: 'fa-duotone fa-flask-gear',
      path: '/autotest',
      role: ['admin'],
      section: 'testing'
    },
    {
      label: translations[language].logout,
      icon: 'fa-duotone fa-right-from-bracket',
      path: 'logout',
      section: 'system'
    }
  ].filter((item) => !item.role || item?.role?.includes(role))
